import React, { useEffect, useState } from "react";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, where } from "firebase/firestore";
import { db } from "../firebase";
import Loader from "../components/Loader";

export default function Advance({ initialFilters }) {
  const [employees, setEmployees] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [form, setForm] = useState({ empId: "", amount: "", month: "", reason: "", date: "", paymentMode: "cash" });
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const showToast = (message, type = "success", timeout = 4000) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, { id, message, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), timeout);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const empSnap = await getDocs(collection(db, "EMPLOYEES"));
        setEmployees(empSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        const advSnap = await getDocs(collection(db, "advance"));
        setAdvances(advSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } finally {
        setLoading(false);
      }
    };
    load();
    // apply initial filters if provided (from overview)
    if (initialFilters) {
      if (initialFilters.employee) setEmployeeFilter(initialFilters.employee);
      if (initialFilters.status) setStatusFilter(initialFilters.status);
      if (initialFilters.fromDate) setFromDate(initialFilters.fromDate);
      if (initialFilters.toDate) setToDate(initialFilters.toDate);
    }
  }, []);

  const onChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const [year, month] = form.month.split("-");
    setSaving(true);
    const monthName = new Intl.DateTimeFormat(undefined, { month: "long" }).format(new Date(+year, +month - 1));
    const paymentDate = form.date ? new Date(form.date) : new Date();
    if (editing) {
      const ref = doc(db, "advance", editing.id);
      await updateDoc(ref, { amount: Number(form.amount), reason: form.reason, month: +month, year: +year, monthName, paymentDate, paymentMode: form.paymentMode });
      setAdvances(prev => prev.map(a => a.id === editing.id ? { ...a, amount: Number(form.amount), reason: form.reason, month: +month, year: +year, monthName, paymentDate, paymentMode: form.paymentMode } : a));
      setEditing(null);
    } else {
      const payload = {
        empId: form.empId,
        amount: Number(form.amount),
        reason: form.reason,
        month: +month,
        year: +year,
        monthName,
        paymentDate,
        paymentMode: form.paymentMode,
        createdAt: new Date(),
        status: "open",
      };
      const ref = await addDoc(collection(db, "advance"), payload);
      setAdvances(prev => [{ id: ref.id, ...payload }, ...prev]);
    }
    setForm({ empId: "", amount: "", month: "", reason: "", date: "", paymentMode: "cash" });
    setShowModal(false);
    setSaving(false);
  };

  const edit = (a) => {
    setEditing(a);
    const dateVal = a.paymentDate ? (a.paymentDate.toDate ? a.paymentDate.toDate().toISOString().slice(0, 10) : new Date(a.paymentDate).toISOString().slice(0, 10)) : '';
    setForm({ empId: a.empId, amount: a.amount, month: `${a.year}-${String(a.month).padStart(2, '0')}`, reason: a.reason || '', date: dateVal, paymentMode: a.paymentMode || 'cash' });
    setShowModal(true);
  };

  const markDebited = async (a) => {
    const ref = doc(db, "advance", a.id);
    try {
      setUpdatingId(a.id);
      await updateDoc(ref, { status: "debited", debitedAt: new Date() });
      setAdvances(prev => prev.map(x => x.id === a.id ? { ...x, status: "debited", debitedAt: new Date() } : x));
      showToast(`Marked debited for ${employees.find(e => (e.uid || e.id) === a.empId)?.name || a.empId}`);
    } catch (err) {
      console.error(err);
      showToast('Failed to mark debited', 'danger');
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteAdvance = async (a) => {
    const empName = employees.find(e => (e.uid || e.id) === a.empId)?.name || '';
    if (!window.confirm(`Delete advance of ${a.amount} for ${empName || 'this employee'}?`)) return;
    try {
      setDeletingId(a.id);
      await deleteDoc(doc(db, "advance", a.id));
      setAdvances(prev => prev.filter(x => x.id !== a.id));
      showToast(`Deleted advance for ${empName || a.empId}`, 'success');
    } catch (err) {
      console.error('Failed to delete advance', err);
      showToast('Failed to delete advance', 'danger');
    } finally {
      setDeletingId(null);
    }
  };

  // compute filtered advances based on UI filters
  const filteredAdvances = advances.filter(a => {
    if (!a) return false;
    if (employeeFilter && a.empId !== employeeFilter) return false;
    if (statusFilter && statusFilter !== 'All' && a.status !== statusFilter) return false;
    if ((fromDate || toDate)) {
      if (!a.paymentDate) return false;
      const pd = a.paymentDate && a.paymentDate.toDate ? a.paymentDate.toDate() : new Date(a.paymentDate);
      if (fromDate) {
        const fd = new Date(fromDate);
        if (pd < fd) return false;
      }
      if (toDate) {
        const td = new Date(toDate);
        td.setHours(23, 59, 59, 999);
        if (pd > td) return false;
      }
    }
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentAdvances = filteredAdvances.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredAdvances.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="px-4">
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="d-flex align-items-center mb-3">
            <h3 className="mb-0">Employee Advances</h3>
            <div className="ms-auto d-flex gap-2">
              <button className="btn btn-outline-secondary" onClick={() => { setEmployeeFilter(""); setStatusFilter("All"); setFromDate(""); setToDate(""); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => { setEditing(null); setForm({ empId: "", amount: "", month: "", reason: "", date: "", paymentMode: "cash" }); setShowModal(true); }} disabled={saving || loading}>{(saving && !showModal) ? <span className="spinner-border spinner-border-sm" /> : 'Add Advance'}</button>
            </div>
          </div>
          {showModal && (
            <div className="modal d-block" tabIndex={-1} role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editing ? 'Edit Advance' : 'Add Advance'}</h5>
                    <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowModal(false)}></button>
                  </div>
                  <div className="modal-body">
                    <form className="row g-2 align-items-end" onSubmit={submit}>
                      <div className="col-md-12">
                        <label className="form-label">Employee</label>
                        <select className="form-select" value={form.empId} onChange={e => onChange('empId', e.target.value)} required>
                          <option value="">Select employee</option>
                          {employees.map(emp => <option key={emp.uid || emp.id} value={emp.uid || emp.id}>{emp.name}</option>)}
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Amount</label>
                        <input type="number" className="form-control" value={form.amount} onChange={e => onChange('amount', e.target.value)} required />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Month</label>
                        <input type="month" className="form-control" value={form.month} onChange={e => onChange('month', e.target.value)} required />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Date</label>
                        <input type="date" className="form-control" value={form.date} onChange={e => onChange('date', e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label">Mode</label>
                        <select className="form-select" value={form.paymentMode} onChange={e => onChange('paymentMode', e.target.value)}>
                          <option value="cash">Cash</option>
                          <option value="gpay">GPay</option>
                          <option value="paytm">Paytm</option>
                          <option value="bank">Bank Transfer</option>
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="form-label">Reason</label>
                        <input className="form-control" value={form.reason} onChange={e => onChange('reason', e.target.value)} />
                      </div>

                      <div className="col-12 text-end mt-2">
                        <button type="button" className="btn btn-secondary me-2" onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
                        <button className="btn btn-primary" disabled={saving}>
                          {saving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                          {editing ? 'Update' : 'Add'}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* toast container */}
          <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1080 }}>
            {toasts.map(t => (
              <div key={t.id} className={`toast show mb-2 ${t.type === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`} role="alert" aria-live="assertive" aria-atomic="true">
                <div className="d-flex">
                  <div className="toast-body">{t.message}</div>
                  <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}></button>
                </div>
              </div>
            ))}
          </div>

          <div className="table-responsive">
            <div className="card mb-3 p-2">
              <div className="row g-2 align-items-center">
                <div className="col-md-4">
                  <select className="form-select form-select-sm" value={employeeFilter} onChange={e => setEmployeeFilter(e.target.value)}>
                    <option value="">All employees</option>
                    {employees.map(emp => <option key={emp.uid || emp.id} value={emp.uid || emp.id}>{emp.name}</option>)}
                  </select>
                </div>
                <div className="col-md-2">
                  <select className="form-select form-select-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="All">All status</option>
                    <option value="open">Open</option>
                    <option value="debited">Debited</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={fromDate} onChange={e => setFromDate(e.target.value)} placeholder="From" />
                </div>
                <div className="col-md-2">
                  <input type="date" className="form-control form-control-sm" value={toDate} onChange={e => setToDate(e.target.value)} placeholder="To" />
                </div>

              </div>
            </div>

            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Month</th>
                  <th>Date</th>
                  <th>Mode</th>
                  <th className="text-end">Amount</th>
                  <th>Reason</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {currentAdvances.map(a => (
                  <tr key={a.id}>
                    <td>{employees.find(e => (e.uid || e.id) === a.empId)?.name || a.empId}</td>
                    <td>{a.monthName || `${a.month}/${a.year}`}</td>
                    <td>{a.paymentDate ? (a.paymentDate.toDate ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(a.paymentDate.toDate()) : new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(a.paymentDate))) : '-'}</td>
                    <td>{a.paymentMode || '-'}</td>
                    <td className="text-end">{new Intl.NumberFormat().format(a.amount)}</td>
                    <td>{a.reason}</td>
                    <td>{a.status}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-1" onClick={() => edit(a)}>Edit</button>
                      {a.status !== 'debited' && <button className="btn btn-sm btn-outline-success" onClick={() => markDebited(a)} disabled={updatingId === a.id}>{updatingId === a.id ? <span className="spinner-border spinner-border-sm" /> : 'Mark Debited'}</button>}
                      <button className="btn btn-sm btn-outline-danger ms-1" onClick={() => deleteAdvance(a)} disabled={deletingId === a.id}>{deletingId === a.id ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> : 'Delete'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredAdvances.length > 0 && (
            <div className={"d-flex justify-content-between align-items-center mt-3"}>
              <div className="d-flex align-items-center gap-2">
                <span>Show</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto" }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
                <span>entries</span>
                <span className="ms-2 text-muted small">
                  Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredAdvances.length)} of {filteredAdvances.length}
                </span>
              </div>

              <nav aria-label="Page navigation">
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
                  </li>
                  {(() => {
                    const totalPages = Math.ceil(filteredAdvances.length / itemsPerPage);
                    return [...Array(totalPages)].map((_, i) => {
                      const p = i + 1;
                      if (
                        p === 1 ||
                        p === totalPages ||
                        (p >= currentPage - 2 && p <= currentPage + 2)
                      ) {
                        return (
                          <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                            <button className="page-link" onClick={() => paginate(p)}>{p}</button>
                          </li>
                        );
                      } else if (
                        (p === currentPage - 3 && currentPage > 4) ||
                        (p === currentPage + 3 && currentPage < totalPages - 3)
                      ) {
                        return <li key={p} className="page-item disabled"><span className="page-link">...</span></li>;
                      }
                      return null;
                    });
                  })()}
                  <li className={`page-item ${currentPage === Math.ceil(filteredAdvances.length / itemsPerPage) ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
}
